import { QueryClient, useMutation, useQueryClient, UseMutationOptions } from '@tanstack/react-query';

import { Document, FileType } from '@/components/pages/Applicant/Applicant.types';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import {
    UpdateFilenameRequest,
    UpdateFilenameResponse,
    updateFilename,
    softDeleteFile,
    UpdateDocumentTypeRequest,
    updateDocumentType,
} from '../endpoints/files';

// Fire-and-forget so callers don't wait on the refetch; invalidateQueries doesn't reject.
const invalidateFileQueries = (
    queryClient: QueryClient,
    applicantId: string,
    queryKeyPrefixes: string[],
) => {
    Promise.all(
        queryKeyPrefixes.map((prefix) =>
            queryClient.invalidateQueries({ queryKey: [prefix, applicantId] }),
        ),
    ).catch(() => {});
};

// The API answers a refused rename with 200 { success: false, message }, not an HTTP error.
export class FilenameRejectedError extends Error {}

interface UpdateFilenameContext {
    previousDocuments?: Document[];
}

interface UpdateDocumentTypeContext {
    previousDocuments?: Document[];
}

export const useUpdateFilenameMutation = (
    applicantId: string,
    options?: Omit<
        UseMutationOptions<
            UpdateFilenameResponse | undefined,
            Error,
            UpdateFilenameRequest,
            UpdateFilenameContext
        >,
        'mutationFn'
    >,
) => {
    const queryClient = useQueryClient();
    const { isRenameResultEnabled } = useFeatureFlags();

    return useMutation<
        UpdateFilenameResponse | undefined,
        Error,
        UpdateFilenameRequest,
        UpdateFilenameContext
    >({
        mutationFn: async (request: UpdateFilenameRequest) => {
            const result = await updateFilename(request);
            if (isRenameResultEnabled && result?.success === false) {
                throw new FilenameRejectedError(result.message ?? '');
            }
            return result;
        },
        onMutate: async (variables) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['applicant-documents', applicantId],
            });

            // Snapshot the previous value
            const previousDocuments = queryClient.getQueryData<Document[]>([
                'applicant-documents',
                applicantId,
            ]);

            // Optimistically update to the new value
            if (previousDocuments) {
                queryClient.setQueryData<Document[]>(
                    ['applicant-documents', applicantId],
                    previousDocuments.map((doc) =>
                        doc.fileId === variables.fileId
                            ? { ...doc, fileName: variables.fileName }
                            : doc,
                    ),
                );
            }

            return { previousDocuments };
        },
        onError: (err, variables, context) => {
            // Rollback on error
            if (context?.previousDocuments) {
                queryClient.setQueryData(
                    ['applicant-documents', applicantId],
                    context.previousDocuments,
                );
            }
            options?.onError?.(err, variables, context);
        },
        onSettled: (data, error, variables, context) => {
            // Always refetch after error or success
            invalidateFileQueries(queryClient, applicantId, ['applicant-documents']);
            options?.onSettled?.(data, error, variables, context);
        },
        onSuccess: (data, variables, context) => {
            options?.onSuccess?.(data, variables, context);
        },
    });
};

export const useUpdateDocumentTypeMutation = (
    applicantId: string,
    options?: Omit<
        UseMutationOptions<boolean | null, Error, UpdateDocumentTypeRequest, UpdateDocumentTypeContext>,
        'mutationFn'
    >,
) => {
    const queryClient = useQueryClient();

    return useMutation<boolean | null, Error, UpdateDocumentTypeRequest, UpdateDocumentTypeContext>({
        mutationFn: async (request: UpdateDocumentTypeRequest) => {
            return await updateDocumentType(request);
        },
        onMutate: async (variables) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['applicant-documents', applicantId],
            });

            // Snapshot the previous value
            const previousDocuments = queryClient.getQueryData<Document[]>([
                'applicant-documents',
                applicantId,
            ]);

            // Optimistically update to the new value
            if (previousDocuments) {
                queryClient.setQueryData<Document[]>(
                    ['applicant-documents', applicantId],
                    previousDocuments.map((doc) =>
                        doc.fileId === variables.fileId
                            ? { ...doc, documentType: FileType[variables.documentType] }
                            : doc,
                    ),
                );
            }

            return { previousDocuments };
        },
        onError: (err, variables, context) => {
            // Rollback on error
            if (context?.previousDocuments) {
                queryClient.setQueryData(
                    ['applicant-documents', applicantId],
                    context.previousDocuments,
                );
            }
            options?.onError?.(err, variables, context);
        },
        onSettled: (data, error, variables, context) => {
            // Always refetch after error or success
            invalidateFileQueries(queryClient, applicantId, ['applicant-documents']);
            options?.onSettled?.(data, error, variables, context);
        },
        onSuccess: (data, variables, context) => {
            options?.onSuccess?.(data, variables, context);
        },
    });
};

export const useSoftDeleteFileMutation = (
    applicantId: string,
    options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>,
) => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, string>({
        mutationFn: (fileId: string) => softDeleteFile(fileId),
        // A deleted file counts as missing from ORP again, and vendor status drops its Au10tix result.
        onSuccess: (data, fileId, context) => {
            invalidateFileQueries(queryClient, applicantId, [
                'applicant-documents',
                'applicant-missing-documents',
                'applicant-vendor-status',
            ]);
            options?.onSuccess?.(data, fileId, context);
        },
        onError: (err, fileId, context) => {
            options?.onError?.(err, fileId, context);
        },
    });
};
